'use client';
import { NEXT_VISIBLE_CARDS_COUNT, PER_SLIDE_OFFSET } from '@/features/advices-slider/utils/consts';
import { parseTransformString } from '@/features/advices-slider/utils/parseTransformString';
import { stringifyTransformObject } from '@/features/advices-slider/utils/stringifyTransformObject';
import { ICatalogCard } from '@/shared/api/getCatalogCards/types';
import { SliderNavigationButton } from '@/shared/ui/SliderNavigationButton';
import { SliderProgress } from '@/shared/ui/SliderProgress';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import { cva } from 'class-variance-authority';
import { transform } from 'motion/react';
import { FC, useState } from 'react';
import { EffectCards } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';
import AdviceIcon from '~/icons/advice.svg';

interface Props {
  className?: string;
  cards: ICatalogCard[];
}

export const AdvicesSlider: FC<Props> = ({ className, cards }) => {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);

  const onSetTranslate = (swiper: SwiperType) => {
    swiper.slides.forEach((slide, idx) => {
      const parsedOriginalTransform = parseTransformString(slide?.style?.transform ?? '');

      const translateX = transform(
        // @ts-expect-error: нет типа
        slide.progress as number,
        [-3, -2, -1, -0.5, 0, 0.5, 1],
        [0, 20, 10, 30, 0, -30, 0] // Более плавные значения
      );

      // Get the slide's offset
      // @ts-expect-error: swiperSlideOffset is not typed
      const slideOffset = slide.swiperSlideOffset;

      // Adjust translateX by subtracting the slide's offset
      // parsedOriginalTransform.translateX = `${translateX - slideOffset}px`;

      const translate3dValues = parsedOriginalTransform.translate3d.split(',').map((value) => value.trim());
      // const translateZ3d = '0px';
      // parsedOriginalTransform.translate3d = `${translateX - slideOffset + 12 * idx}px, ${translateY3d}, ${translateZ3d}`;
      parsedOriginalTransform.translate3d = `${translateX - slideOffset + 6 * idx}px, ${translate3dValues[1]}, ${parseInt(translate3dValues[2].replace('px', '')) / 100}px`;
      // parsedOriginalTransform.rotateZ = '0deg';

      parsedOriginalTransform.rotate =
        transform(
          // @ts-expect-error: нет типа
          slide.progress as number,
          [-1, -0.5, 0, 0.5, 1],
          [0, 2, 0, -2, 0] // Более плавные значения для вращения
        ) + 'deg';

      parsedOriginalTransform.scale = '1';

      slide.style.transform = stringifyTransformObject(parsedOriginalTransform);
    });
  };

  return (
    <Swiper
      className={twMergeExt(cvaRoot(), className)}
      modules={[EffectCards]}
      loop={true}
      effect={'cards'}
      cardsEffect={{
        slideShadows: false,
        rotate: false,
        perSlideOffset: PER_SLIDE_OFFSET,
      }}
      onSetTranslate={onSetTranslate}
      onSwiper={setSwiper}
    >
      {cards?.map((i, index) => (
        <SwiperSlide key={index}>
          <div
            className={cvaSlideInner()}
            style={{
              backgroundColor: bgColors[index % bgColors.length],
              width: cards.length === 1 ? '100%' : `calc(100% - ${NEXT_VISIBLE_CARDS_COUNT} * ${PER_SLIDE_OFFSET}px)`,
            }}
          >
            <AdviceIcon className={'w-2.4 sm:w-4.8 h-2.4 sm:h-4.8'} />
            <div
              className={cvaSlideContent()}
              dangerouslySetInnerHTML={{
                __html: i.text,
              }}
            />

            {cards.length > 1 ? (
              <div className={cvaControls()}>
                <SliderProgress swiper={swiper} />
                <div className={cvaControlButtons()}>
                  <SliderNavigationButton
                    swiper={swiper}
                    color={'outlinedOnColor'}
                    className="bg-transparent"
                    direction={'prev'}
                    canBeDisabled={false}
                  />
                  <SliderNavigationButton
                    swiper={swiper}
                    className="bg-transparent"
                    color={'outlinedOnColor'}
                    direction={'next'}
                    canBeDisabled={false}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

const bgColors = ['#DFFAD5', '#D4EAFF', '#FFE4F4'];

const cvaRoot = cva(['AdvicesSlider-cvaRoot', 'w-full', 'z-10']);

const cvaSlideInner = cva([
  'AdvicesSlider-cvaSlideInner',
  'flex flex-col',
  'h-full',
  'p-1.6 sm:p-3.2 rounded-[3.2rem]',
]);

const cvaSlideContent = cva([
  'AdvicesSlider-cvaSlideContent',
  'mt-1.2 sm:mt-3.2',
  'font-secondary text-extrah6 sm:text-h4',
]);

const cvaControls = cva([
  'AdvicesSlider-cvaControls',
  'flex justify-between sm:items-center flex-col sm:flex-row gap-1.4 sm:gap-0',
  'mt-auto',
]);

const cvaControlButtons = cva(['AdvicesSlider-cvaControlButtons', 'flex items-center gap-0.8']);
